import { SearchOutlined } from '@ant-design/icons';
import { Button, Checkbox, Input } from 'antd';
import { useEffect, useMemo, useState } from 'react';

import { QUICK_ACCESS_MAX_SLOTS } from '~/constants/quickAccess';
import type { QuickAccessItem } from '~/hooks/useQuickAccess';

import * as S from './QuickAccessSettingsModal.styled';

type QuickAccessSettingsModalProps = {
  isOpen: boolean;
  options: QuickAccessItem[];
  pinnedKeys: string[];
  onClose: () => void;
  onSave: (keys: string[]) => void;
};

const normalize = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

export default function QuickAccessSettingsModal({
  isOpen,
  options,
  pinnedKeys,
  onClose,
  onSave,
}: QuickAccessSettingsModalProps) {
  const [selectedKeys, setSelectedKeys] = useState<string[]>(pinnedKeys);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!isOpen) return;

    setSelectedKeys(pinnedKeys);
    setSearch('');
  }, [isOpen, pinnedKeys]);

  const filteredOptions = useMemo(() => {
    const term = normalize(search.trim());
    if (!term) return options;

    return options.filter(
      option =>
        normalize(option.title).includes(term) || normalize(option.parentTitle).includes(term),
    );
  }, [options, search]);

  const isFull = selectedKeys.length >= QUICK_ACCESS_MAX_SLOTS;

  const toggleKey = (key: string) => {
    if (selectedKeys.includes(key)) {
      setSelectedKeys(selectedKeys.filter(selected => selected !== key));
      return;
    }

    if (isFull) return;

    setSelectedKeys([...selectedKeys, key]);
  };

  return (
    <S.SettingsModal
      visible={isOpen}
      title="Personalizar acesso rápido"
      onCancel={onClose}
      destroyOnClose
      footer={
        <S.ModalFooter>
          <S.FooterLinkButton>
            <Button type="link" onClick={() => setSelectedKeys([])}>
              Restaurar padrão
            </Button>
          </S.FooterLinkButton>
          <S.FooterActions>
            <Button onClick={onClose}>Cancelar</Button>
            <Button type="primary" onClick={() => onSave(selectedKeys)}>
              Salvar
            </Button>
          </S.FooterActions>
        </S.ModalFooter>
      }
    >
      <Input
        allowClear
        prefix={<SearchOutlined />}
        placeholder="Buscar menu"
        value={search}
        onChange={event => setSearch(event.target.value)}
      />

      <S.SettingsHint>
        {`${selectedKeys.length} de ${QUICK_ACCESS_MAX_SLOTS} selecionados. Sem seleção, exibimos os atalhos padrão.`}
      </S.SettingsHint>

      <S.OptionList>
        {filteredOptions.map(option => {
          const isChecked = selectedKeys.includes(option.key);
          const isDisabled = !isChecked && isFull;

          return (
            <S.Option key={option.key} data-disabled={isDisabled}>
              <Checkbox
                checked={isChecked}
                disabled={isDisabled}
                onChange={() => toggleKey(option.key)}
              >
                <S.OptionTexts>
                  <S.OptionTitle>{option.title}</S.OptionTitle>
                  {!!option.parentTitle && <S.OptionParent>{option.parentTitle}</S.OptionParent>}
                </S.OptionTexts>
              </Checkbox>
            </S.Option>
          );
        })}

        {!filteredOptions.length && <S.EmptyOptions>Nenhum menu encontrado.</S.EmptyOptions>}
      </S.OptionList>
    </S.SettingsModal>
  );
}
